import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Package, Mail, Truck, Save, User, Building, Phone, Mail as MailIcon, Bell, Upload } from 'lucide-react'
import { deliveryApi } from '../services/api'
import api from '../services/api'
import { showError } from '../utils/showError'
import PageHeader from '../components/common/PageHeader'
import LoadingSpinner from '../components/common/LoadingSpinner'

const CreateDelivery = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [buildings, setBuildings] = useState([])
  const [users, setUsers] = useState([])
  const [buildingUnits, setBuildingUnits] = useState([])
  const [searchingUsers, setSearchingUsers] = useState(false)
  const [userSearchTerm, setUserSearchTerm] = useState('')
  const [errors, setErrors] = useState({})
  
  const [formData, setFormData] = useState({
    // Required fields
    recipientUserId: '',
    buildingId: '',
    deliveryType: 1, // Package by default
    
    // Optional recipient details
    recipientName: '',
    recipientAddress: '',
    recipientUnitNumber: '',
    recipientFloorNumber: '',
    recipientPhone: '',
    recipientEmail: '',
    
    // Optional sender details
    senderAddress: '',
    senderName: '',
    serialNumber: '',
    
    // Building & Unit
    buildingUnitId: '',
    
    // Status
    isDelivered: false,
    deliveryDate: '',
    isCollected: false,
    collectedDate: '',
    
    // Notification settings
    notificationMethods: [1], // Email by default
    
    // AI and validation settings
    isAIGenerated: false,
    aiPrompt: '',
    skipValidation: false,
    forceCreateForInactiveUser: false,
    
    
    // Attachments
    attachmentIds: []
  })

  useEffect(() => {
    loadBuildings()
  }, [])

  useEffect(() => {
    if (formData.buildingId) {
      loadBuildingUnits(formData.buildingId)
    }
  }, [formData.buildingId])

  useEffect(() => {
    if (userSearchTerm.length >= 2) {
      searchUsers(userSearchTerm)
    } else {
      setUsers([])
    }
  }, [userSearchTerm])

  const loadBuildings = async () => {
    try {
      const response = await api.get('/Building', { params: { pageSize: 1000 } })
      setBuildings(response.data.items || [])
    } catch (error) {
      console.error('Error loading buildings:', error)
    }
  }

  const loadBuildingUnits = async (buildingId) => {
    try {
      const response = await api.get(`/Building/${buildingId}/units`)
      setBuildingUnits(response.data.items || [])
    } catch (error) {
      console.error('Error loading building units:', error)
      setBuildingUnits([])
    }
  }

  const searchUsers = async (searchTerm) => {
    try {
      setSearchingUsers(true)
      const response = await api.get('/Users', { 
        params: { 
          searchString: searchTerm,
          pageSize: 20
        }
      })
      setUsers(response.data.items || [])
    } catch (error) {
      console.error('Error searching users:', error)
      setUsers([])
    } finally {
      setSearchingUsers(false)
    }
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }

    // Auto-populate recipient details when user is selected
    if (field === 'recipientUserId' && value) {
      const selectedUser = users.find(u => u.userId === value)
      if (selectedUser) {
        setFormData(prev => ({
          ...prev,
          recipientName: `${selectedUser.firstName || ''} ${selectedUser.lastName || ''}`.trim(),
          recipientEmail: selectedUser.email || '',
          recipientPhone: selectedUser.phoneNumber || ''
        }))
      }
    }
  }

  const handleNotificationMethodChange = (method, isChecked) => {
    setFormData(prev => ({
      ...prev,
      notificationMethods: isChecked
        ? [...prev.notificationMethods, method]
        : prev.notificationMethods.filter(m => m !== method)
    }))
  }

  const validateForm = () => {
    const newErrors = {}

    // Required fields
    if (!formData.recipientUserId) {
      newErrors.recipientUserId = 'Recipient user is required'
    }
    if (!formData.buildingId) {
      newErrors.buildingId = 'Building is required'
    }
    if (!formData.deliveryType) {
      newErrors.deliveryType = 'Delivery type is required'
    }

    // Conditional validations
    if (formData.isDelivered && !formData.deliveryDate) {
      newErrors.deliveryDate = 'Delivery date is required when marked as delivered'
    }
    if (formData.isCollected && !formData.collectedDate) {
      newErrors.collectedDate = 'Collection date is required when marked as collected'
    }
    if (formData.isCollected && !formData.isDelivered) {
      newErrors.isDelivered = 'Cannot be collected without being delivered first'
    }

    // Email validation
    if (formData.recipientEmail && !/\S+@\S+\.\S+/.test(formData.recipientEmail)) {
      newErrors.recipientEmail = 'Invalid email format'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    try {
      setLoading(true)
      
      const submitData = {
        ...formData,
        // Convert string IDs to appropriate types
        buildingUnitId: formData.buildingUnitId ? parseInt(formData.buildingUnitId) : undefined,
        
        // Ensure dates are in proper format
        deliveryDate: formData.deliveryDate || undefined,
        collectedDate: formData.collectedDate || undefined,
        announcementScheduleDate: formData.announcementScheduleDate || undefined,
        
        // Remove empty strings and convert to proper types
        attachmentIds: formData.attachmentIds.filter(id => id).map(id => parseInt(id))
      }

      // Remove undefined fields
      Object.keys(submitData).forEach(key => {
        if (submitData[key] === '' || submitData[key] === undefined) {
          delete submitData[key]
        }
      })

      console.log('Submitting delivery data:', submitData)
      
      const response = await deliveryApi.create(submitData)
      console.log('Delivery created successfully:', response.data)
      
      navigate(`/deliveries/${response.data.deliveryId}`)
    } catch (error) {
      console.error('Error creating delivery:', error)
      showError(error)

      // Handle API validation errors
      if (error.response?.data?.errors) {
        const apiErrors = {}
        Object.keys(error.response.data.errors).forEach(key => {
          apiErrors[key.toLowerCase()] = error.response.data.errors[key][0]
        })
        setErrors(apiErrors)
      }
    } finally {
      setLoading(false)
    }
  }

  const getDeliveryTypeIcon = (type) => {
    switch (type) {
      case 1: return <Package className="w-5 h-5" />
      case 2: return <Mail className="w-5 h-5" />
      default: return <Truck className="w-5 h-5" />
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={
          <div className="flex items-center">
            <button
              onClick={() => navigate('/deliveries')}
              className="mr-4 p-2 rounded-lg hover:bg-gray-100"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            Create New Delivery
          </div>
        }
      />

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Basic Information</h3>
          </div>
          <div className="px-6 py-4 space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Delivery Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Delivery Type *
                </label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="deliveryType"
                      value={1}
                      checked={formData.deliveryType === 1}
                      onChange={(e) => handleInputChange('deliveryType', parseInt(e.target.value))}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                    />
                    <Package className="ml-2 mr-1 w-4 h-4" />
                    <span className="text-sm text-gray-700">Package</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="deliveryType"
                      value={2}
                      checked={formData.deliveryType === 2}
                      onChange={(e) => handleInputChange('deliveryType', parseInt(e.target.value))}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                    />
                    <Mail className="ml-2 mr-1 w-4 h-4" />
                    <span className="text-sm text-gray-700">Mail</span>
                  </label>
                </div>
                {errors.deliveryType && (
                  <p className="mt-1 text-sm text-red-600">{errors.deliveryType}</p>
                )}
              </div>

              {/* Building */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Building *
                </label>
                <select
                  value={formData.buildingId}
                  onChange={(e) => handleInputChange('buildingId', e.target.value)}
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                >
                  <option value="">Select a building</option>
                  {buildings.map(building => (
                    <option key={building.buildingId} value={building.buildingId}>
                      {building.name} - {building.address}
                    </option>
                  ))}
                </select>
                {errors.buildingId && (
                  <p className="mt-1 text-sm text-red-600">{errors.buildingId}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Sender Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sender Name
                </label>
                <input
                  type="text"
                  value={formData.senderName}
                  onChange={(e) => handleInputChange('senderName', e.target.value)}
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  placeholder="Amazon, FedEx, etc."
                />
              </div>

              {/* Serial Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Serial/Tracking Number
                </label>
                <input
                  type="text"
                  value={formData.serialNumber}
                  onChange={(e) => handleInputChange('serialNumber', e.target.value)}
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  placeholder="Tracking or reference number"
                />
              </div>
            </div>

            {/* Sender Address */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sender Address
              </label>
              <textarea
                value={formData.senderAddress}
                onChange={(e) => handleInputChange('senderAddress', e.target.value)}
                rows={2}
                className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                placeholder="Sender's address"
              />
            </div>
          </div>
        </div>

        {/* Recipient Information */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Recipient Information</h3>
          </div>
          <div className="px-6 py-4 space-y-6">
            {/* User Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search Recipient *
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={userSearchTerm}
                  onChange={(e) => setUserSearchTerm(e.target.value)}
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  placeholder="Search by name or email..."
                />
                {searchingUsers && (
                  <div className="absolute right-3 top-2">
                    <LoadingSpinner size="sm" />
                  </div>
                )}
              </div>
              
              {/* User Dropdown */}
              {users.length > 0 && (
                <div className="mt-2 max-h-60 overflow-y-auto border border-gray-300 rounded-md bg-white shadow-lg">
                  {users.map(user => (
                    <button
                      key={user.userId}
                      type="button"
                      onClick={() => {
                        handleInputChange('recipientUserId', user.userId)
                        setUserSearchTerm(`${user.firstName} ${user.lastName}`)
                        setUsers([])
                      }}
                      className="w-full text-left px-4 py-2 hover:bg-gray-50 border-b border-gray-200 last:border-b-0"
                    >
                      <div className="text-sm font-medium text-gray-900">
                        {user.firstName} {user.lastName}
                      </div>
                      <div className="text-sm text-gray-500">{user.email}</div>
                    </button>
                  ))}
                </div>
              )}
              {errors.recipientUserId && (
                <p className="mt-1 text-sm text-red-600">{errors.recipientUserId}</p>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Recipient Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Recipient Name
                </label>
                <input
                  type="text"
                  value={formData.recipientName}
                  onChange={(e) => handleInputChange('recipientName', e.target.value)}
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  placeholder="Auto-filled from selected user"
                />
              </div>

              {/* Building Unit */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Building Unit
                </label>
                <select
                  value={formData.buildingUnitId}
                  onChange={(e) => handleInputChange('buildingUnitId', e.target.value)}
                  disabled={!formData.buildingId}
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-100"
                >
                  <option value="">Select a unit (optional)</option>
                  {buildingUnits.map(unit => (
                    <option key={unit.buildingUnitId} value={unit.buildingUnitId}>
                      {unit.unitNumber} - {unit.unitType}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Unit Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Unit Number
                </label>
                <input
                  type="text"
                  value={formData.recipientUnitNumber}
                  onChange={(e) => handleInputChange('recipientUnitNumber', e.target.value)}
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  placeholder="101, 2A, etc."
                />
              </div>

              {/* Floor Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Floor Number
                </label>
                <input
                  type="text"
                  value={formData.recipientFloorNumber}
                  onChange={(e) => handleInputChange('recipientFloorNumber', e.target.value)}
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  placeholder="1, 2, Ground, etc."
                />
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Recipient Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Recipient Email
                </label>
                <input
                  type="email"
                  value={formData.recipientEmail}
                  onChange={(e) => handleInputChange('recipientEmail', e.target.value)}
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  placeholder="Auto-filled from selected user"
                />
                {errors.recipientEmail && (
                  <p className="mt-1 text-sm text-red-600">{errors.recipientEmail}</p>
                )}
              </div>

              {/* Recipient Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Recipient Phone
                </label>
                <input
                  type="tel"
                  value={formData.recipientPhone}
                  onChange={(e) => handleInputChange('recipientPhone', e.target.value)}
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  placeholder="Auto-filled from selected user"
                />
              </div>
            </div>

            {/* Recipient Address */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Recipient Address
              </label>
              <textarea
                value={formData.recipientAddress}
                onChange={(e) => handleInputChange('recipientAddress', e.target.value)}
                rows={2}
                className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                placeholder="Recipient's full address"
              />
            </div>
          </div>
        </div>

        {/* Delivery Status */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Delivery Status</h3>
          </div>
          <div className="px-6 py-4 space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Is Delivered */}
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isDelivered}
                    onChange={(e) => handleInputChange('isDelivered', e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700">Mark as Delivered</span>
                </label>
                {formData.isDelivered && (
                  <div className="mt-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Delivery Date *
                    </label>
                    <input
                      type="datetime-local"
                      value={formData.deliveryDate}
                      onChange={(e) => handleInputChange('deliveryDate', e.target.value)}
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                    {errors.deliveryDate && (
                      <p className="mt-1 text-sm text-red-600">{errors.deliveryDate}</p>
                    )}
                  </div>
                )}
                {errors.isDelivered && (
                  <p className="mt-1 text-sm text-red-600">{errors.isDelivered}</p>
                )}
              </div>

              {/* Is Collected */}
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isCollected}
                    onChange={(e) => handleInputChange('isCollected', e.target.checked)}
                    disabled={!formData.isDelivered}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded disabled:opacity-50"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700">Mark as Collected</span>
                </label>
                {formData.isCollected && (
                  <div className="mt-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Collection Date *
                    </label>
                    <input
                      type="datetime-local"
                      value={formData.collectedDate}
                      onChange={(e) => handleInputChange('collectedDate', e.target.value)}
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                    {errors.collectedDate && (
                      <p className="mt-1 text-sm text-red-600">{errors.collectedDate}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Notification Settings */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Notification Settings</h3>
          </div>
          <div className="px-6 py-4 space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notification Methods
              </label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.notificationMethods.includes(1)}
                    onChange={(e) => handleNotificationMethodChange(1, e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <MailIcon className="ml-2 mr-1 w-4 h-4" />
                  <span className="text-sm text-gray-700">Email</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.notificationMethods.includes(2)}
                    onChange={(e) => handleNotificationMethodChange(2, e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <Phone className="ml-2 mr-1 w-4 h-4" />
                  <span className="text-sm text-gray-700">SMS</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.notificationMethods.includes(3)}
                    onChange={(e) => handleNotificationMethodChange(3, e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <Bell className="ml-2 mr-1 w-4 h-4" />
                  <span className="text-sm text-gray-700">Push Notification</span>
                </label>
              </div>
            </div>
          </div>
        </div>


        {/* Advanced Options */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Advanced Options</h3>
          </div>
          <div className="px-6 py-4 space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.skipValidation}
                    onChange={(e) => handleInputChange('skipValidation', e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Skip validation</span>
                </label>
              </div>

              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.forceCreateForInactiveUser}
                    onChange={(e) => handleInputChange('forceCreateForInactiveUser', e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Force create for inactive user</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Submit Buttons */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/deliveries')}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <LoadingSpinner size="sm" className="mr-2" />
                Creating...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Create Delivery
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

export default CreateDelivery